import {
    type DateRange,
    formatDate,
    formatNumber,
    formatRange,
    GetBalancesParams,
    getCurrentMonthRange,
    getRange,
    toDateString,
    type VacationBalance,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { popover } from "../../../directives/popover";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/date-range-picker";
import "../../../elements/popover";
import { app } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { routeProperty, Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../components/recalcuate-results-dialog";
import type { RecalculateResultsDialog } from "../components/recalcuate-results-dialog";
import "../components/reset-balances-dialog";
import type { ResetBalancesDialog } from "../components/reset-balances-dialog";

@customElement("ptc-employees-vacation-balance-single")
export class EmployeesVacationBalanceSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<employeeId>\d+)\/vacation-balance$/;

    @routeProperty({ arg: "employeeId", type: Number })
    employeeId: number;

    defaultRange = () => {
        const firstContract = this._employee?.firstContract;
        if (!firstContract) {
            return getCurrentMonthRange();
        }
        const from = getRange(firstContract.start, this._resolution || "month").from;
        const today = toDateString(new Date());
        const contractEnd = this._employee?.latestContract?.end;
        let to = !contractEnd || today < contractEnd ? today : contractEnd;
        to = getRange(to, this._resolution || "month").to;
        return { from, to };
    };

    @routeProperty({ param: "resolution", default: () => "month" })
    private _resolution: "month" | "week" | "year";

    @singleton("ptc-reset-balances-dialog")
    private _resetBalancesDialog: ResetBalancesDialog;

    @singleton("ptc-recalculate-results-dialog")
    private _recalculateResultsDialog: RecalculateResultsDialog;

    get routeTitle() {
        return `Urlaubskonto: ${this._employee?.name}`;
    }

    @state()
    private _balances: VacationBalance[] = [];

    @state()
    private _loading = false;

    private get _employee() {
        return app.getEmployee(this.employeeId);
    }

    handleRefresh() {
        void this.load();
    }

    updated(changes: Map<string, unknown>) {
        if (
            changes.has("employeeId") ||
            changes.has("dateFrom") ||
            changes.has("dateTo") ||
            changes.has("_resolution")
        ) {
            // update in the background
            void this.load();
        }
    }

    async load() {
        if (this._loading || !this._resolution) {
            return;
        }

        this._loading = true;

        try {
            const resolution = this._resolution;
            this._balances = (
                await app.api.getVacationBalances(
                    new GetBalancesParams({
                        from: this.dateFrom,
                        to: this.dateTo,
                        resolution,
                        filters: [{ type: "employeeId", value: this.employeeId }],
                    })
                )
            ).sort((a, b) => (a.from > b.from ? -1 : 1));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _editBalancesReset(balances: VacationBalance) {
        const edited = await this._resetBalancesDialog.show({
            date: balances.from,
            filters: [{ type: "employeeId", value: balances.employeeId }],
        });

        if (edited) {
            // update in the background
            void this.load();
        }
    }

    private async _resetBalances() {
        const edited = await this._resetBalancesDialog.show({
            date: getRange(new Date(), this._resolution || "month").from,
            filters: [{ type: "employeeId", value: this._employee!.id }],
        });

        if (edited) {
            // update in the background
            void this.load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: column;
            }

            .scroller {
                flex: 1;
                overflow: auto;
            }

            table {
                border-collapse: separate;
                display: block;
            }

            thead {
                display: block;
                position: sticky;
                top: 0;
                z-index: 2;
            }

            tbody {
                display: block;
                margin-bottom: 1em;
                box-sizing: border-box;
            }

            tr {
                display: grid;
                grid-template-columns: var(--column-width-date) repeat(5, 8rem);
                align-items: center;
                width: 100%;
            }

            tbody > tr:not(:last-of-type) {
                border-bottom: solid 1px var(--shade-2);
            }

            th,
            td {
                padding: 0.5em;
                vertical-align: middle;
            }

            td {
                text-align: right;
            }

            thead {
                font-weight: bold;
                background: var(--color-bg);
                text-align: center;
            }

            tr.subbalance > th {
                padding-left: 2em;
            }

            ptc-ui-balance {
                vertical-align: middle;
            }
        `,
    ];

    private _renderBalances(balances: VacationBalance[], sub = false): TemplateResult {
        balances.sort((a, b) => (a.from > b.from ? -1 : 1));
        return html`
            ${balances.map(
                (balance, i) => html`
                    <tr
                        class="${sub ? "smaller subbalance" : ""} click"
                        @click=${() =>
                            this.go(`employees/${this.employeeId}/time`, {
                                from: balance.from,
                                to: balance.to,
                            })}
                    >
                        <th class="${sub ? "smaller" : ""}">${formatRange(balance)}</th>
                        <td class="${sub ? "smaller" : ""}">
                            <ptc-ui-balance .value=${balance.reset ?? balance.carry}></ptc-ui-balance>
                        </td>
                        <td>${formatNumber(balance.nominal)}</td>
                        <td class="${sub ? "smaller" : ""}">
                            ${balance.adjustments
                                ? html`<sup
                                      class="tinier"
                                      ${popover(
                                          html`
                                              <table class="simple">
                                                  <thead>
                                                      <tr>
                                                          <th style="text-align: left;">Ursache</th>
                                                          <th>Tage</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody>
                                                      <tr>
                                                          <th>
                                                              <ptc-ui-icon name="icon.time-entry.work"></ptc-ui-icon>
                                                              Genommen
                                                          </th>
                                                          <td>${formatNumber(balance.taken)}</td>
                                                      </tr>
                                                      <tr>
                                                          <th>
                                                              <ptc-ui-icon name="icon.object.booking"></ptc-ui-icon>
                                                              Urlaubsbuchungen
                                                          </th>
                                                          <td>${formatNumber(balance.adjustments)}</td>
                                                      </tr>
                                                  </tbody>
                                              </table>
                                          `,
                                          {
                                              trigger: "hover",
                                              class: "text-left-aligning tooltip non-interactive",
                                          }
                                      )}
                                  >
                                      <ptc-ui-icon name="icon.object.booking"></ptc-ui-icon>
                                  </sup> `
                                : ""}
                            ${formatNumber(balance.actual)}
                        </td>
                        <td class="${sub ? "smaller" : ""}">
                            <ptc-ui-balance
                                .value=${balance.difference}
                                iconStyle="up-down"
                                class="semibold"
                            ></ptc-ui-balance>
                        </td>
                        <td class="${sub ? "smaller" : ""}">
                            <ptc-ui-balance class="bold" .value=${balance.balance}></ptc-ui-balance>
                        </td>
                    </tr>

                    ${typeof balance.subBalances !== "undefined" ? this._renderBalances(balance.subBalances, true) : ""}
                    ${typeof balance.reset === "number" && (!sub || i < balances.length - 1)
                        ? html`
                              <tr class="click subbalance" @click=${() => this._editBalancesReset(balance)}>
                                  <th class="bold smaller" style="grid-column: span 3;">
                                      <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Konto zurückgesetzt am
                                      ${formatDate(balance.from)}
                                  </th>
                                  <td class="smaller" style="grid-column: span 3;">
                                      <ptc-ui-balance .value=${balance.carry} class="semibold"></ptc-ui-balance>
                                      <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                                      <ptc-ui-balance .value=${balance.reset} class="bold"></ptc-ui-balance>
                                  </td>
                              </tr>
                          `
                        : ""}
                `
            )}
        `;
    }

    render() {
        return html`
            <div class="center-aligning horizontal layout padded border-bottom">
                <ptc-date-range-picker
                    @range-selected=${(e: CustomEvent<DateRange>) =>
                        this.go(null, { ...this.router.params, ...e.detail })}
                    .range=${this.dateRange}
                ></ptc-date-range-picker>

                <div class="left-margined">Auflösung:</div>

                <select
                    class="slim transparent bold"
                    .value=${live(this._resolution)}
                    @change=${(e: Event) =>
                        this.go(null, { ...this.router.params, resolution: (e.target as HTMLSelectElement).value })}
                >
                    <option .value=${"week"}>Woche</option>
                    <option .value=${"month"}>Monat</option>
                    <option .value=${"year"}>Jahr</option>
                </select>

                <div class="stretch"></div>

                ${app.hasPermission("manage.exports.ledgers_vacation")
                    ? html`<button
                          class="transparent slim right-margined"
                          title="Urlaubszeiten Exportieren"
                          @click=${() =>
                              this.go("exports/balances-vacation-excel", {
                                  from: this.dateFrom,
                                  to: this.dateTo,
                                  "entity-filters": JSON.stringify([{ type: "employeeId", value: this.employeeId }]),
                              })}
                      >
                          <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                      </button>`
                    : nothing}

                <button id="moreMenuButton" class="slim transparent">
                    <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                </button>

                <ptc-ui-popover for="moreMenuButton" placement="bottom" class="popover-menu" data-popover="close">
                    <button @click=${this._resetBalances}>
                        <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Konten Zurücksetzen
                    </button>
                    <button
                        @click=${async () => {
                            const recalculated = await this._recalculateResultsDialog.show({
                                filters: [{ type: "employeeId", value: this._employee!.id }],
                            });
                            if (recalculated) {
                                // update in the background
                                void this.load();
                            }
                        }}
                    >
                        <ptc-ui-icon name="icon.action.recalculate"></ptc-ui-icon> Konten Neu Berechnen
                    </button>
                </ptc-ui-popover>
            </div>

            <div class="scroller">
                <div class="double-padded">
                    <div>
                        <table class="fill-horizontally">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th scope="col">Übertrag</th>
                                    <th scope="col">Zuwachs</th>
                                    <th scope="col">Verbraucht</th>
                                    <th scope="col">Differenz</th>
                                    <th scope="col">Saldo</th>
                                </tr>
                            </thead>

                            <tbody>
                                ${this._renderBalances(this._balances)}
                            </tbody>
                        </table>
                    </div>

                    <div
                        class="fullbleed center-aligning center-justifying vertical layout scrim"
                        ?hidden=${!this._loading}
                    >
                        <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                    </div>
                </div>
            </div>
        `;
    }
}
